import { readAttributionContext, writeAttributionContext } from "@attribution/storage";

/**
 * Relative URL of the server-side Tapfiliate click proxy exposed by
 * funnel-api. The proxy forwards the request to `frstre.com/event/`
 * server-to-server, bypassing CORS restrictions that block direct
 * browser requests from domains not whitelisted by Tapfiliate (e.g.
 * `companio.chat`).
 */
const TAPFILIATE_CLICK_PROXY_URL = "/api/tc/click";

type TapfiliateClickResponse = {
  vid?: string;
};

const persistVid = (vid: string): void => {
  const freshContext = readAttributionContext();
  if (!freshContext) return;
  writeAttributionContext({
    ...freshContext,
    partners: { ...freshContext.partners, tapVid: vid },
  });
};

/**
 * Creates a Tapfiliate click via the funnel-api server-side proxy which
 * forwards the request to Tapfiliate's tracking endpoint. This replicates
 * what `tap("detect")` does in Tapfiliate's JS library without needing
 * to load the external script, and avoids CORS errors by routing through
 * our own API.
 *
 * On success, persists the returned visitor ID (`vid`) into the attribution
 * envelope as `partners.tapVid` so it can be sent to the backend during
 * user creation.
 *
 * No-op when:
 * - No `tapfiliateAccountId` is provided (app has no Tapfiliate configured)
 * - No `affiliateId` is present in the attribution context
 * - `tapVid` is already set (click already created)
 * - The fetch fails (network error, non-JSON response, etc.)
 */
export const createTapfiliateClick = async ({
  tapfiliateAccountId,
}: {
  tapfiliateAccountId: string | null | undefined;
}): Promise<string | undefined> => {
  if (typeof window === "undefined") return undefined;
  if (!tapfiliateAccountId) return undefined;

  const context = readAttributionContext();
  const affiliateId = context?.partners?.affiliateId;
  if (!affiliateId) return undefined;

  if (context?.partners?.tapVid) return context.partners.tapVid;

  try {
    const response = await fetch(TAPFILIATE_CLICK_PROXY_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        acc: tapfiliateAccountId,
        refc: affiliateId,
        ref: document.referrer || null,
        cup: window.location.href || null,
        int: "javascript",
      }),
    });

    if (!response.ok) return undefined;

    const data = (await response.json()) as TapfiliateClickResponse;
    if (!data.vid) return undefined;

    persistVid(data.vid);
    return data.vid;
  } catch {
    // Network or parse failure — attribution is telemetry, never a blocker.
    return undefined;
  }
};
